import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GameBoard from "@/components/GameBoard";
import { Keyboard } from "@/components/keyboard/Keyboard";
import InstructionsOverlay from "@/components/InstructionsOverlay";
import InProgressGameOverlay from "@/components/InProgressGameOverlay";
import { SharedSequenceConflictOverlay } from "@/components/SharedSequenceConflictOverlay";
import { DebugDateOverlay } from "@/components/debug/DebugDateOverlay";
import { useDebugDate } from "@/contexts/DebugDateContext";
import { GameStatus } from "@/lib/types/GameTypes";
import { getMaxAttemptsForLevel } from "@/components/game/GameLogic";
import { handleGameLoss, handleGameWinFinal } from "@/components/game/managers/GameStatusManager";
import { initializeGameState } from "@/components/game/managers/GameInitializationManager";
import { stateLoadingQueue } from "@/components/game/managers/StateLoadingQueueManager";
import { GameInitializationResult } from "@/components/game/types/InitializationTypes";
import { getDebugParams, copyDebugLogs } from "@/lib/debugUtils";
import { clearCachedOverlayState } from "@/components/game/managers/OverlayManager";
import { OverlayStateCoordinator } from "@/components/game/managers/OverlayStateCoordinator";
import * as CookieManager from "@/lib/managers/CookieManager";
import { modeAwareStorage } from "@/lib/managers/ModeAwareStorageManager";
import { useGameMode } from "@/contexts/GameModeContext";
import { useSimplifiedWordSequence } from "@/hooks/useSimplifiedWordSequence";
import { Header } from "@/components/layout/Header";
import { SEO } from "@/components/SEO";
import { useGameResetListener } from "@/lib/gameReset";
import { directCookieAccess } from "@/lib/storage/StorageAdapter";
import { sharedSequenceStateManager } from "@/components/game/managers/SharedSequenceStateManager";
import { CompletionStateManager } from "@/components/game/managers/CompletionStateManager";
import { startGameSession } from "@/lib/playerTracking";
import { useViewMode } from "@/contexts/ViewModeContext";
import { LivestreamCamera } from "@/components/livestream/LivestreamCamera";
const GamePage = () => {
  const {
    debugDate
  } = useDebugDate();
  const {
    currentMode
  } = useGameMode();
  const {
    wordSequence
  } = useSimplifiedWordSequence();
  const navigate = useNavigate();
  const { isLivestream, exitLivestream } = useViewMode();
  // Synchronously detect completion from cookies on first mount so the
  // completion screen renders instantly when returning to a finished game.
  const initialCompletionRef = useRef<boolean>(
    (() => {
      try {
        return CompletionStateManager.shouldShowCompletion(currentMode);
      } catch {
        return false;
      }
    })()
  );
  const initialGameStatusRef = useRef<GameStatus>(
    (() => {
      try {
        const status = modeAwareStorage.getCookieForMode('game_status', currentMode) as GameStatus | null;
        if (initialCompletionRef.current && (status === 'won' || status === 'lost')) {
          return status;
        }
      } catch {}
      return 'playing';
    })()
  );

  const [currentLevel, setCurrentLevel] = useState(1);
  const [gameStatus, setGameStatus] = useState<GameStatus>(initialGameStatusRef.current);
  const [attempts, setAttempts] = useState(0);
  const [currentWord, setCurrentWord] = useState("");
  const [gameWordSequence, setGameWordSequence] = useState<string[]>([]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showGameCompletion, setShowGameCompletion] = useState(initialCompletionRef.current);
  const [immediateCompletion, setImmediateCompletion] = useState(initialCompletionRef.current);
  const [showInProgressOverlay, setShowInProgressOverlay] = useState(false);
  const [showSharedSequenceConflict, setShowSharedSequenceConflict] = useState(false);
  const [conflictSequenceId, setConflictSequenceId] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [levelAttempts, setLevelAttempts] = useState<number[]>([]);
  const [guessHistory, setGuessHistory] = useState([]);
  const [keyboardHandlers, setKeyboardHandlers] = useState({
    onKeyPress: (_key: string) => {},
    onSubmit: () => {},
    keyboardStatus: {},
    currentGuess: ""
  });
  const initializationInProgressRef = useRef(false);
  const keyHandlersRef = useRef(keyboardHandlers);
  const wasModeSwitch = useRef(localStorage.getItem('babblegram_force_mode_change') === 'true');
  const pageLoadChecked = useRef(false);
  const fromWelcomePageChecked = useRef(false);

  // Add a game reset listener
  useGameResetListener(() => {
    console.log('GamePage component received game reset event, reinitializing game');

    // Reset state
    setCurrentLevel(1);
    setGameStatus('playing');
    setAttempts(0);
    setShowGameCompletion(false);
    setImmediateCompletion(false);
    clearCachedOverlayState();

    // Force reinitialization on next render
    setIsInitialized(false);
    initializationInProgressRef.current = false;

    // Word sequence will be refreshed automatically by useEffect
  });

  // CRITICAL FIX: Sync wordSequence from hook to local state
  useEffect(() => {
    if (wordSequence && wordSequence.length > 0) {
      console.log('GamePage: Syncing new word sequence from hook:', wordSequence);

      // Check if this is a new sequence (different from current one)
      const isNewSequence = JSON.stringify(wordSequence) !== JSON.stringify(gameWordSequence);
      setGameWordSequence(wordSequence);

      // If it's a new sequence, reset to level 1 and use first word
      if (isNewSequence) {
        console.log('GamePage: New sequence detected, resetting to level 1 and clearing completion state');
        setCurrentLevel(1);
        setCurrentWord(wordSequence[0]);
        setAttempts(0);
        setGameStatus('playing');
        setShowGameCompletion(false);
        setImmediateCompletion(false);

        // Game state is now managed independently per mode - no clearing needed
      } else {
        // Set the current word based on current level for existing sequence
        const wordIndex = Math.min(currentLevel - 1, wordSequence.length - 1);
        const newCurrentWord = wordSequence[wordIndex] || wordSequence[0];
        setCurrentWord(newCurrentWord);
      }
      console.log('GamePage: Updated current word to:', wordSequence[0]);
    }
  }, [wordSequence, currentLevel, gameWordSequence]);

  // Handle mode switch events
  useEffect(() => {
    const handleModeSwitched = () => {
      console.log('GamePage: Mode switch detected');

      // Reset overlay state
      clearCachedOverlayState();

      // Force reinitialization on next render
      setIsInitialized(false);
      initializationInProgressRef.current = false;

      // Set flag to indicate this was a mode switch
      wasModeSwitch.current = true;
    };
    window.addEventListener('babblegram_mode_switched', handleModeSwitched);
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitched);
    };
  }, []);

  // CRITICAL FIX: Check for from_welcome_page flag in both localStorage and sessionStorage
  useEffect(() => {
    if (!fromWelcomePageChecked.current) {
      fromWelcomePageChecked.current = true;
      const fromWelcomeLS = localStorage.getItem('from_welcome_page') === 'true';
      const fromWelcomeSS = sessionStorage.getItem('from_welcome_page') === 'true';
      console.log('GamePage: checking from_welcome_page flags', {
        fromWelcomeLS,
        fromWelcomeSS,
        timestamp: new Date().toISOString()
      });

      // If this session came from welcome page, ensure it's tracked in sessionStorage too
      // This prevents redirect loops while allowing proper redirect on direct access/reload
      if (fromWelcomeLS) {
        sessionStorage.setItem('from_welcome_page', 'true');

        // Clear the localStorage flag since it's now in sessionStorage (persists for tab session)
        localStorage.removeItem('from_welcome_page');

        // Also set for the current game mode to ensure mode-specific tracking
        const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
        localStorage.removeItem(`${prefix}from_welcome_page`);
      }
    }
  }, [currentMode]);

  // CRITICAL FIX: Check for page reload and redirect - Always run this effect on mount
  useEffect(() => {
    // Only run once to avoid infinite redirect loops
    if (!pageLoadChecked.current) {
      pageLoadChecked.current = true;
      console.log('Checking for page reload redirect on initial mount');
      const hasWelcomeFlag = sessionStorage.getItem('from_welcome_page') === 'true';
      const isReload = window.performance.navigation?.type === window.performance.navigation?.TYPE_RELOAD;
      const isDirectAccess = !document.referrer || document.referrer === window.location.href;
      console.log('Page load detection:', {
        hasWelcomeFlag,
        isReload,
        isDirectAccess,
        referrer: document.referrer,
        currentUrl: window.location.href,
        timestamp: new Date().toISOString()
      });

      // Check for shared sequence in URL - skip redirect if present
      const sequenceId = new URLSearchParams(window.location.search).get('sequence');
      const hasSharedSequence = !!sequenceId;

      // Only redirect if user hasn't come from welcome page this session AND it's a reload/direct access AND no shared sequence
      if (!hasWelcomeFlag && (isReload || isDirectAccess) && !hasSharedSequence) {
        console.log('Page reload or direct access detected without welcome flag - redirecting to welcome');
        navigate('/welcome');
        return;
      } else if (hasSharedSequence) {
        console.log('Shared sequence detected, allowing direct access');
      }
    }
  }, [navigate]);

  // Record an anonymous game session (daily unique players / completion tracking)
  useEffect(() => {
    if (initialCompletionRef.current) return; // returning to an already-finished game
    startGameSession(currentMode as 'daily' | 'practice' | 'custom');
  }, [currentMode]);



  // Game initialization effect
  useEffect(() => {
    const initializeGame = async () => {
      if (isInitialized || initializationInProgressRef.current) {
        console.log('Game already initialized or initialization in progress, skipping');
        return;
      }

      // Wait for word sequence to be ready
      if (!wordSequence || wordSequence.length === 0) {
        console.log('Word sequence not ready, waiting...', {
          hasSequence: !!wordSequence,
          length: wordSequence?.length || 0,
          timestamp: new Date().toISOString()
        });
        return;
      }
      console.log('Starting game initialization');
      initializationInProgressRef.current = true;
      setIsLoading(true);
      setError(null);
      try {
        // Check for force game reset
        const forceGameReset = localStorage.getItem('babblegram_force_game_reset') === 'true';
        if (forceGameReset) {
          console.log('Force game reset detected, clearing reset flag');
          localStorage.removeItem('babblegram_force_game_reset');
          clearCachedOverlayState();
        }

        // Check for midnight transition flag and force completion state to false
        const midnightTransition = localStorage.getItem('babblegram_midnight_transition') === 'true';
        if (midnightTransition) {
          console.log('Midnight transition detected - forcing completion state to false');
          localStorage.removeItem('babblegram_midnight_transition');
        }

        // Load game state using the simplified system  
        const gameState = await stateLoadingQueue.enqueue<GameInitializationResult>(() => initializeGameState(debugDate || new Date(), wordSequence));
        console.log('Game state loaded:', gameState);

        // Override completion state if midnight transition occurred
        if (midnightTransition) {
          gameState.showGameCompletion = false;
          console.log('Overriding showGameCompletion to false due to midnight transition');
        }

        // Handle shared sequence conflicts
        if (gameState.showSharedSequenceConflict) {
          console.log('Shared sequence conflict detected');
          setShowSharedSequenceConflict(true);
          setConflictSequenceId(gameState.sequenceId || '');
          setIsInitialized(true);
          initializationInProgressRef.current = false;
          setIsLoading(false);
          return;
        }
        setCurrentLevel(gameState.currentLevel);
        setCurrentWord(gameState.currentWord);
        setGameWordSequence(gameState.wordSequence);
        setShowInstructions(gameState.showInstructions);
        setAttempts(gameState.attempts);
        setGameStatus(gameState.gameStatus);
        setLevelAttempts(gameState.levelAttempts || []);
        setGuessHistory(gameState.guessHistory || []);
        if (gameState.showGameCompletion && !showGameCompletion) {
          console.log('Setting immediate completion screen display');
          setShowGameCompletion(true);
          setImmediateCompletion(true);
        }

        // CRITICAL FIX: Use overlay state from initialization result (already determined atomically)
        // No need to call determineOverlayState again - that would cause duplicate reads
        if (!gameState.showGameCompletion && !gameState.showSharedSequenceConflict && !wasModeSwitch.current && !initialCompletionRef.current) {
          console.log('[GamePage] Using overlay state from initialization:', gameState.showInProgressOverlay);
          setShowInProgressOverlay(gameState.showInProgressOverlay);
          
          // If NOT showing overlay, mark session as active (user is playing)
          if (!gameState.showInProgressOverlay) {
            OverlayStateCoordinator.markSessionActive();
          }
        } else if (wasModeSwitch.current) {
          // Clear mode switch flag after handling
          wasModeSwitch.current = false;
          setShowInProgressOverlay(false);
          OverlayStateCoordinator.markSessionActive();
        }
        setIsInitialized(true);
        
        // CRITICAL FIX: Mark page as loaded AFTER initialization completes
        // This ensures all future isPageReload() calls return false
        OverlayStateCoordinator.markPageAsLoaded();
        
        console.log('Game initialization completed');
      } catch (error) {
        console.error('Error initializing game:', error);
        setError(error instanceof Error ? error : new Error('Failed to initialize game'));
        toast.error("Failed to load game. Please try again.");
      } finally {
        initializationInProgressRef.current = false;
        setIsLoading(false);
      }
    };

    // Only initialize if we have a word sequence
    if (wordSequence && wordSequence.length > 0) {
      initializeGame();
    }
    return () => {
      stateLoadingQueue.clearQueue();
    };
  }, [isInitialized, currentMode, debugDate, showGameCompletion, wordSequence]);

  // Handle new user detection and instructions
  useEffect(() => {
    const detectNewUser = async () => {
      if (gameStatus !== 'playing' || showGameCompletion || isLoading || error) {
        console.log('Skipping new user detection:', {
          gameStatus,
          showGameCompletion,
          isLoading,
          error: !!error
        });
        return;
      }
      try {
        console.log('Checking if user is new');
        const isNewUser = !modeAwareStorage.getCookie('has_played_before');
        if (isNewUser) {
          console.log('New user detected, will show instructions');
          setShowInstructions(true);
          modeAwareStorage.setCookie('has_played_before', 'true');
        } else {
          console.log('Returning user, hiding instructions');
          setShowInstructions(false);
        }
      } catch (error) {
        console.error('Error in new user detection:', error);
      }
    };
    detectNewUser();
  }, [gameStatus, showGameCompletion, isLoading, error]);

  // Keyboard input handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isLivestream) {
        event.preventDefault();
        exitLivestream('escape_key');
        return;
      }

      const hasVisibleInstructions = document.querySelector('[data-game-input-blocking-overlay="instructions"]') !== null;

      // Don't handle key events while a visible overlay is shown or the game is validating.
      // The parent instruction flag can remain true after the child decides it has
      // nothing to display, so use the rendered panel as the source of truth.
      if (hasVisibleInstructions || showGameCompletion || showInProgressOverlay || isValidating) {
        return;
      }

      // Use the current handlers from the ref
      const currentHandlers = keyHandlersRef.current;
      if (event.key === 'Enter') {
        event.preventDefault();
        currentHandlers.onSubmit();
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        currentHandlers.onKeyPress('Backspace');
      } else if (/^[a-zA-Z]$/.test(event.key)) {
        event.preventDefault();
        currentHandlers.onKeyPress(event.key.toLowerCase());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showInstructions, showGameCompletion, showInProgressOverlay, isValidating, currentWord, isLivestream, exitLivestream]);
  const handleCorrectGuess = useCallback(async () => {
    try {
      const nextLevel = currentLevel + 1;
      if (nextLevel <= 4) {
        setCurrentLevel(nextLevel);
        const nextWord = gameWordSequence[nextLevel - 1];
        setCurrentWord(nextWord);
      } else {
        await handleGameWinFinal(setGameStatus, setShowGameCompletion, currentLevel, true);
      }
    } catch (error) {
      console.error('Error handling correct guess:', error);
      toast.error("Error updating game progress");
    }
  }, [currentLevel, gameWordSequence]);
  const handleGameOver = useCallback(async () => {
    try {
      console.log('Game over called at level:', currentLevel);

      // Store the failed level in cookie before handling game loss
      modeAwareStorage.setCookie('failed_level', currentLevel.toString());
      await handleGameLoss(setGameStatus, setShowGameCompletion, currentLevel);
    } catch (error) {
      console.error('Error handling game over:', error);
      toast.error("Error handling game completion");
    }
  }, [currentLevel]);
  const handleAttempt = useCallback(() => {
    setAttempts(prev => prev + 1);
  }, []);
  const registerKeyboardHandlers = useCallback((handlers: any) => {
    console.log('Registering keyboard handlers');
    setKeyboardHandlers(handlers);
    keyHandlersRef.current = handlers;
  }, []);

  // Handle key press from virtual keyboard
  const handleKeyPress = useCallback((key: string) => {
    if (keyboardHandlers.onKeyPress) {
      keyboardHandlers.onKeyPress(key);
    }
  }, [keyboardHandlers]);

  // Handle submit from virtual keyboard
  const handleKeyboardSubmit = useCallback(() => {
    if (keyboardHandlers.onSubmit) {
      keyboardHandlers.onSubmit();
    }
  }, [keyboardHandlers]);
  const handleInProgressOverlayClose = () => {
    console.log('In-progress overlay closed by user');
    setShowInProgressOverlay(false);

    // Clear the overlay state so it doesn't show again this session
    clearCachedOverlayState();
  };
  const handlePlaySharedSequence = () => {
    console.log('🎯 User chose to play shared sequence');

    // Set flag to use shared sequence
    localStorage.setItem('babblegram_practice_mode_use_shared_sequence', 'true');

    // Remove sequence parameter from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('sequence');
    window.history.replaceState({}, '', url.toString());
    setShowSharedSequenceConflict(false);

    // Force reinitialization
    setIsInitialized(false);
    initializationInProgressRef.current = false;

    // Word sequence will be refreshed automatically by useEffect
  };
  const handleContinueExistingGame = () => {
    console.log('🎯 User chose to continue existing game');

    // Mark this shared sequence as handled
    const handledKey = `babblegram_shared_sequence_handled_${conflictSequenceId}`;
    localStorage.setItem(handledKey, 'true');

    // Remove sequence parameter from URL and continue existing game
    const url = new URL(window.location.href);
    url.searchParams.delete('sequence');
    window.history.replaceState({}, '', url.toString());
    setShowSharedSequenceConflict(false);
  };
  const handleCopyLogs = async () => {
    try {
      const success = await copyDebugLogs();
      if (success) {
        toast.success("Console logs copied to clipboard");
      } else {
        toast.error("No debug logs found to copy");
      }
    } catch (error) {
      console.error('Error copying debug logs from GamePage:', error);
      toast.error("Failed to copy debug logs");
    }
  };
  // Keep the livestream stage mounted during an in-place game reset so the
  // browser does not tear down and request the camera stream again.
  if (isLoading && (!isLivestream || !currentWord)) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-white">Loading game...</div>
      </div>;
  }
  if (error) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-red-400">Error: {error.message}</div>
      </div>;
  }
  const gameBoard = (
    <div className={`relative ${isLivestream ? "livestream-board-wrap" : ""}`}>
      <div className={`mx-auto w-full overflow-hidden rounded-xl shadow-xl dot-pattern ${currentMode === 'practice' ? 'dot-pattern-purple game-outline-practice' : 'game-outline-daily'} ${isLivestream ? 'livestream-board' : 'max-w-[360px] sm:max-w-[380px]'}`}>
        <div className="relative z-10 p-1 space-y-1 rounded-xl">
          <GameBoard currentWord={currentWord} attempts={attempts} maxAttempts={getMaxAttemptsForLevel(currentWord.length)} onCorrectGuess={handleCorrectGuess} onGameOver={handleGameOver} onAttempt={handleAttempt} registerKeyboardHandlers={registerKeyboardHandlers} immediateCompletion={immediateCompletion} compactLayout={isLivestream} />
        </div>
      </div>
    </div>
  );

  const keyboard = currentWord && (
    <Keyboard keyboardStatus={keyboardHandlers.keyboardStatus} onKeyPress={handleKeyPress} onSubmit={handleKeyboardSubmit} isValidating={isValidating} currentGuess={keyboardHandlers.currentGuess} currentWord={currentWord} compactLayout={isLivestream} />
  );

  const modeLabel = currentMode === 'practice'
    ? <span className="text-purple-400 text-sm font-medium">Practice Mode</span>
    : <span className="text-primary text-sm font-medium">Daily Challenge</span>;

  return <div className={`${isLivestream ? "livestream-page" : "p-1 pt-16 py-[69px]"} flex-grow flex-col items-center justify-start bg-gray-900 w-full overflow-y-auto`}>
      <SEO
        title="Play BabbleGram – Today's Daily Word Puzzle"
        description="Play today's BabbleGram puzzle. Solve 3, 4, and 5-letter words that build on each other and keep your streak alive."
        path="/game"
      />
      <h1 className="sr-only">Play today's BabbleGram daily word puzzle</h1>
      <DebugDateOverlay />
      
      {getDebugParams().debug === 'date' && <Button onClick={handleCopyLogs} size="sm" variant="outline" className="fixed right-2 top-14 z-[100000] bg-yellow-600 hover:bg-yellow-700 text-white">
          <Bug className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Copy Logs</span>
        </Button>}
      
      {showInstructions && <InstructionsOverlay />}
      
      {showInProgressOverlay && <InProgressGameOverlay isOpen={showInProgressOverlay} onClose={handleInProgressOverlayClose} />}
      
      {showSharedSequenceConflict && <SharedSequenceConflictOverlay isOpen={showSharedSequenceConflict} onPlayShared={handlePlaySharedSequence} onContinueExisting={handleContinueExistingGame} sequenceId={conflictSequenceId} />}
      

      <div className={isLivestream ? "livestream-stage" : "w-full max-w-md mx-auto mt-0 my-0 pt-0 sm:pt-0 pb-2 sm:pb-2"}>
        {isLivestream && <LivestreamCamera key="livestream-camera" onExit={() => exitLivestream("camera_exit")} />}
        <div key="game-layout" className={isLivestream ? "livestream-game-row" : "contents"}>
          <div className={isLivestream ? "livestream-play-area" : "contents"}>
            {!isLivestream && <Header />}
            <div key="game-board-slot" className="contents">{gameBoard}</div>
            <div className={isLivestream ? "livestream-keyboard-wrap" : "flex pb-0 mt-3 sm:mt-4 w-full max-w-[400px] sm:max-w-[580px] mx-auto"}>{keyboard}</div>
            <div className={isLivestream ? "livestream-mode-label" : "flex w-full max-w-[400px] sm:max-w-[580px] mx-auto my-0 mb-56 items-center"}>
              <div className={isLivestream ? "contents" : "text-center w-full mt-1 pt-0 mb-1"}>{modeLabel}</div>
            </div>
          </div>
          {isLivestream && (
            <aside className="livestream-brand-rail" aria-label="BabbleGram">
              <img src="/lovable-uploads/f8f44bc9-ce01-42f1-98ff-b6aad7a934e9.png" alt="BabbleGram" className="livestream-side-logo" />
            </aside>
          )}
        </div>
      </div>
    </div>;
};
export default GamePage;